import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import moment from "moment";

const statusColors = { 1: "default", 2: "primary", 3: "warning", 4: "success", 5: "error" };

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";

  const { data: invoice, isLoading, isError, error } = useFetchObjects(
    id ? ["invoice", id] : null,
    id ? `invoices/${id}/` : "",
    token
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !invoice) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load invoice"}
      </Alert>
    );
  }

  const lines = invoice.invoice_lines || [];

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/finance/invoices")} sx={{ mb: 2 }}>
        {t("finance.back", { defaultValue: "Back to Invoices" })}
      </Button>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
          {t("finance.invoice", { defaultValue: "Invoice" })} #{invoice.id}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary">{t("finance.patient")}</Typography>
                <Typography variant="h6">{invoice.patient_name_display || `#${invoice.patient}`}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary">{t("finance.date")}</Typography>
                <Typography variant="h6">
                  {invoice.invoice_date ? moment(invoice.invoice_date).format("YYYY-MM-DD") : "—"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary">{t("finance.status")}</Typography>
                <Chip label={invoice.status_display} color={statusColors[invoice.status] || "default"} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary">{t("finance.total")}</Typography>
                <Typography variant="h6">
                  {invoice.total_amount != null ? Number(invoice.total_amount).toFixed(2) : "0.00"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          {t("finance.lineItems", { defaultValue: "Line Items" })}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.200" }}>
                <TableCell><strong>{t("finance.item")}</strong></TableCell>
                <TableCell align="right"><strong>{t("finance.quantity")}</strong></TableCell>
                <TableCell align="right"><strong>{t("finance.unitPrice")}</strong></TableCell>
                <TableCell align="right"><strong>{t("finance.total")}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {t("finance.noLineItems", { defaultValue: "No line items" })}
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.item_name}</TableCell>
                    <TableCell align="right">{line.quantity}</TableCell>
                    <TableCell align="right">{line.unit_price != null ? Number(line.unit_price).toFixed(2) : "—"}</TableCell>
                    <TableCell align="right">{line.total != null ? Number(line.total).toFixed(2) : "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Typography variant="h6">
            {t("finance.grandTotal", { defaultValue: "Total" })}:{" "}
            {invoice.total_amount != null ? Number(invoice.total_amount).toFixed(2) : "0.00"}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
